import { CollegeProductionRow } from '../types/CollegeProductionData';

export type RankingType = 'passing' | 'rushing' | 'receiving' | 'defense' | 'punting' | 'kicking';

export interface RankingLink {
  type: RankingType;
  label: string;
  url: string;
}

export interface PlayerRankingLinks {
  primary?: RankingLink;
  additional: RankingLink[];
  all: RankingLink[];
}

const CBTN_BASE_URL = 'https://coachesbythenumbers.com';

const RANKING_DEFINITIONS: Array<{ type: RankingType; label: string; path: string }> = [
  { type: 'passing', label: 'Passing Rankings', path: '/player-passing-analytics/' },
  { type: 'rushing', label: 'Rushing Rankings', path: '/player-rushing-analytics/' },
  { type: 'receiving', label: 'Receiving Rankings', path: '/player-receiving-analytics/' },
  { type: 'defense', label: 'Defense Rankings', path: '/player-defense-analytics/' },
  { type: 'punting', label: 'Punting Rankings', path: '/player-punting-analytics/' },
  { type: 'kicking', label: 'Kicking Rankings', path: '/player-kicking-analytics/' },
];

function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function hasKickAttempt(row: CollegeProductionRow): boolean {
  const record = row as unknown as Record<string, unknown>;
  const attempts =
    toNumber(record.kickingXPA) +
    toNumber(record.kickingXPAtt) +
    toNumber(record.kickingFGA) +
    toNumber(record.kickingFGAtt) +
    toNumber(record.kickoff) +
    toNumber(record.kickingKickoff) +
    toNumber(record.kickingKickoffs);

  return attempts >= 1;
}

export function buildPlayerRankingLinks(rows: CollegeProductionRow[] = []): PlayerRankingLinks {
  if (!rows.length) {
    return { primary: undefined, additional: [], all: [] };
  }

  const qualifies: Record<RankingType, boolean> = {
    passing: rows.some((row) => toNumber(row.passingAtt) >= 5),
    rushing: rows.some((row) => toNumber(row.rushingAtt) >= 5),
    receiving: rows.some((row) => toNumber(row.receivingTar) >= 5),
    defense: rows.some((row) => toNumber(row.defenseSoloTackles) + toNumber(row.defenseAssistedTackles) >= 1),
    punting: rows.some((row) => toNumber(row.punt) >= 1),
    kicking: rows.some((row) => hasKickAttempt(row)),
  };

  const all = RANKING_DEFINITIONS
    .filter((definition) => qualifies[definition.type])
    .map((definition) => ({
      type: definition.type,
      label: definition.label,
      url: `${CBTN_BASE_URL}${definition.path}`,
    }));

  if (!all.length) {
    return { primary: undefined, additional: [], all };
  }

  const primary = all[0];
  const additional = all.slice(1);

  return { primary, additional, all };
}
